/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Returns true when the event target lives inside a Select portal menu.
 * Select options render on document.body, outside Dialog/Sheet content.
 */
export function isSelectPortalEventTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('[data-select-portal]'));
}

/**
 * Returns true when the event target lives inside the Dev Cockpit UI.
 * The cockpit is portaled outside AdaptiveFormShell Dialog/Sheet content.
 */
export function isDevCockpitEventTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('[data-dev-cockpit]'));
}

type DismissableEvent = {
  target: EventTarget | null;
  preventDefault: () => void;
};

/**
 * Keeps AdaptiveFormShell open when the user interacts with body-mounted UI
 * that is not part of the shell (Select portals, Dev Cockpit fillers).
 * Without this, Radix treats those clicks as outside dismiss.
 */
export function preventSelectPortalDismiss(event: DismissableEvent): void {
  if (isSelectPortalEventTarget(event.target) || isDevCockpitEventTarget(event.target)) {
    event.preventDefault();
  }
}

/**
 * Whether *target* is a combobox whose suggestion list is open.
 *
 * @param target - The element the key was pressed on.
 * @returns True for a combobox with `aria-expanded="true"`.
 */
export function isOpenComboboxTarget(target: EventTarget | null): boolean {
  return target instanceof Element
    && target.getAttribute('role') === 'combobox'
    && target.getAttribute('aria-expanded') === 'true';
}

/**
 * Keeps Escape on an open autocomplete from closing the whole form.
 *
 * Radix reads Escape on the document before the input sees it, so the
 * input's own "close the list" handler ran after the dialog had already
 * closed and the typed form was gone. Escape on an open list now only
 * closes the list; a second Escape closes the form as before.
 *
 * @param event - The Escape keydown Radix is about to dismiss on.
 */
export function preventOpenComboboxEscape(event: DismissableEvent): void {
  if (isOpenComboboxTarget(event.target)) {
    event.preventDefault();
  }
}
